import { Experiment, GrowthBook, Result } from '@growthbook/growthbook-react';
import { emitGAEvent } from '../../utils/ga-event-helpers';
import ApiInterface from './ApiInterface';

// TPD-295: single module-level GrowthBook instance, primed early (from init())
// and read imperatively from Redux actions on the redirect money path. The
// legacy per-render hook (app/hooks/use-growthbook.ts) and declarative wrapper
// were removed — two independent evaluators of the same flag caused the races
// this replaces. Note: enableDevMode must stay OFF — in SDK 0.32 it disables
// cache-served features (every load would need a live CDN round-trip, dropping
// the fastest searchers) and exposes window._growthbook, which allows forcing
// a variation without an exposure being logged.

const GROWTHBOOK_API_HOST = 'https://cdn.growthbook.io';

let growthbook: GrowthBook | null = null;

// Assignment hashes on the stable uid (100y cookie) ONLY — imid (1h cookie)
// in the hash would re-bucket the same visitor between visits. The exposure
// row identity is a separate concern: viewed_experiment.user_id uses the
// `${uid}${imid}` identity (decision 2026-07-05, aligned with the GA user_id
// and the backend's own format; the exposure→revenue join normalizes it on
// the data side).
let exposureUid: string | null = null;
let exposureImid: string | null = null;
let exposureVert: string | null = null;

// Exposures are logged only for experiments registered here. viewed_experiment
// feeds the exposure===eligible analysis assumption, so an auto/visual
// experiment published later on the same client key must not silently write
// rows outside any eligibility gate. Consequence: the GrowthBook tracking key
// MUST equal the registered feature key — a mismatch shows up as zero
// exposures plus the console warning below (caught by the staging gate).
const trackedExperimentKeys = new Set<string>();

export const registerTrackedExperiment = (featureKey: string) => {
	trackedExperimentKeys.add(featureKey);
};

const trackExposure = (experiment: Experiment<unknown>, result: Result<unknown>) => {
	// Fire-and-forget: the front tab opens in a new window, so this tab stays
	// alive long enough for the POST to leave. Errors are swallowed — exposure
	// logging must never affect the redirect.
	try {
		if (!trackedExperimentKeys.has(experiment.key)) {
			console.log('growthbook: ignoring exposure for untracked experiment', experiment.key);
			return;
		}
		const vert = exposureVert || 'hotels';
		const exposure = {
			experiment_id: experiment.key,
			variation_id: result.variationId.toString(),
			event_timestamp: new Date().toISOString(),
			// The `${uid}${imid}` identity — same as the GA user_id and the
			// backend's viewed_experiment format (decision 2026-07-05). The 2025
			// run died because this 40-char identity was joined against the
			// 32-char conversion_event uid as-is; the join now normalizes on the
			// data side (e.g. SUBSTR(user_id,1,32) in the assignment query).
			user_id: `${exposureUid}${exposureImid || ''}`,
		};
		ApiInterface.instance.experiment(vert, exposure).catch(() => {});
		// Mirror the exposure into GA4 (BFP pattern): joins with the rest of the
		// GA/BQ export under the same uid+imid user_id.
		emitGAEvent('experiment_viewed', vert, {
			experiment_id: exposure.experiment_id,
			variation_id: exposure.variation_id,
		});
	} catch (e) {
		console.log('growthbook exposure logging failed', e);
	}
};

export const primeGrowthbook = () => {
	if (typeof window === 'undefined') return;
	if (!process.env.NEXT_PUBLIC_GROWTHBOOK_API_KEY) return;
	if (growthbook) return;

	try {
		growthbook = new GrowthBook({
			apiHost: GROWTHBOOK_API_HOST,
			clientKey: process.env.NEXT_PUBLIC_GROWTHBOOK_API_KEY,
			// Keep long-lived tabs on the SSE stream so the GrowthBook kill-switch
			// reaches them without a reload (PRD user story 8).
			subscribeToChanges: true,
			trackingCallback: trackExposure,
		});
		growthbook.loadFeatures().catch(() => {});
	} catch (e) {
		growthbook = null;
		console.log('growthbook init failed', e);
	}
};

export const setGrowthbookUser = ({ uid, imid, vert }: { uid?: string; imid?: string; vert?: string }) => {
	try {
		if (!growthbook || !uid) return;
		exposureUid = uid;
		exposureImid = imid || exposureImid;
		exposureVert = vert || exposureVert;
		// Hash on uid only; url enables GrowthBook URL targeting if ever needed.
		// client:'front' is the owner-scoping attribute: experiments carrying the
		// `client = front` targeting condition are invisible to every other SDK
		// consumer (the backend Go client does not set it), which keeps the
		// frontend the single evaluator without touching backend code/config.
		growthbook.setAttributes({ id: uid, url: window.location.href, client: 'front' });
	} catch (e) {
		console.log('growthbook setAttributes failed', e);
	}
};

// Ready = the flag itself is evaluable AND the current-session uid attribute
// is set. `growthbook.ready` alone is not enough: SDK 0.32 flips it to true
// even when the features fetch fails (empty feature set), which would report
// gb_ready:true while no experiment can run. Anything less than ready means
// the visitor silently stays out of the experiment (no exposure, no
// treatment) — fail-open by design.
export const isGrowthbookReady = (featureKey: string): boolean => {
	try {
		if (!growthbook || !growthbook.ready || !exposureUid) return false;
		return growthbook.getFeatures()[featureKey] !== undefined;
	} catch (e) {
		console.log('growthbook readiness check failed', e);
		return false;
	}
};

export const isFeatureOn = (featureKey: string): boolean => {
	try {
		// Synchronous in-memory evaluation. For flags with an experiment rule this
		// is the bucketing point: trackingCallback fires here for BOTH arms.
		return Boolean(growthbook && growthbook.isOn(featureKey));
	} catch (e) {
		console.log('growthbook feature evaluation failed', e);
		return false;
	}
};
